import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { EnvelopeIcon, LockClosedIcon, ArrowRightIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(false);
  const { login } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if registration is enabled
  useEffect(() => {
    const checkRegistrationStatus = async () => {
      try {
        const response = await api.get('/auth/registration-status');
        setRegistrationEnabled(response.data.registrationEnabled);
      } catch (error) {
        // If can't check, assume disabled
        setRegistrationEnabled(false);
      }
    };
    checkRegistrationStatus();
  }, []);
  
  // Show success message if redirected from registration
  useEffect(() => {
    if (location.state?.registrationSuccess) {
      toast.success(location.state.message || 'Registration successful! Check your email.', {
        duration: 6000
      });
      // Clear the state to prevent showing again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const result = await login(email, password);
    
    if (result.success) {
      toast.success('Welcome back!');
      navigate('/dashboards');
    } else {
      toast.error(result.error);
    }
    
    setIsLoading(false);
  };
  
  return (
    <div className="animate-slide-up">
      <h2 className="text-2xl font-display font-bold text-white mb-2">
        Welcome back
      </h2>
      <p className="text-surface-400 mb-8">
        Sign in to your account to continue
      </p>
      
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-surface-300 mb-2">
            Email address
          </label>
          <div className="relative">
            <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-500" />
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              className="w-full pl-11 pr-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-white placeholder:text-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            />
          </div>
        </div>
        
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-surface-300 mb-2">
            Password
          </label>
          <div className="relative">
            <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-500" />
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              className="w-full pl-11 pr-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-white placeholder:text-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            />
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-600 focus:ring-primary-500" />
            <span className="text-sm text-surface-400">Remember me</span>
          </label>
          <Link to="/auth/forgot-password" className="text-sm text-primary-400 hover:text-primary-300 transition-colors">
            Forgot password?
          </Link>
        </div>
        
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-500 text-white font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              Sign in
              <ArrowRightIcon className="w-4 h-4" />
            </>
          )}
        </button>
      </form>
      
      <div className="mt-6 space-y-2">
        <p className="text-center text-surface-400 text-sm">
          Don't have an account?{' '}
          {registrationEnabled ? (
            <Link to="/register" className="text-primary-400 hover:text-primary-300 transition-colors">
              Create an account
            </Link>
          ) : (
            <span className="text-surface-500">Contact your administrator</span>
          )}
        </p>
        <p className="text-center text-surface-400 text-sm">
          <Link to="/pricing" className="text-primary-400 hover:text-primary-300 transition-colors">
            View Pricing
          </Link>
        </p>
      </div>
    </div>
  );
}

